import PublicLayout from '../../layouts/PublicLayout';
import { POLICY_VERSIONS, POLICY_LAST_UPDATED } from '../../lib/policies';

export default function PrivacyPage() {
  return (
    <PublicLayout>
      <div className="container-main section-padding max-w-4xl">
        <div className="prose prose-slate max-w-none">
          <div className="mb-8 pb-6 border-b border-slate-200">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              Privacy Policy
            </h1>
            <p className="text-slate-500">
              Last Updated: {POLICY_LAST_UPDATED} • Version {POLICY_VERSIONS.privacy}
            </p>
          </div>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              1. Introduction
            </h2>
            <p className="text-slate-600 mb-4">
              Southwest Florida Vacation Rentals ("SWFL Rentals", "we", "us", or "our") respects 
              your privacy and is committed to protecting your personal data. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use 
              our platform.
            </p>
            <p className="text-slate-600 mb-4">
              <strong>SWFL Rentals operates as a marketplace platform</strong> that facilitates 
              connections between property owners and travelers. We are not a property owner or 
              payment processor.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              2. Information We Collect
            </h2>
            <h3 className="font-semibold text-slate-800 mb-2">Personal Information</h3>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Name and contact information (email, phone number)</li>
              <li>Account credentials</li>
              <li>Payment information (processed by Stripe, not stored by us)</li>
              <li>Property address (for Hosts)</li>
              <li>Government ID (for verification purposes)</li>
            </ul>
            
            <h3 className="font-semibold text-slate-800 mb-2">Usage Information</h3>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Search queries and browsing history on our platform</li>
              <li>Device information and IP address</li>
              <li>Cookies and similar tracking technologies</li>
              <li>Communication records between users</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              3. How We Use Your Information
            </h2>
            <p className="text-slate-600 mb-4">We use collected information to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Provide and improve our platform services</li>
              <li>Process bookings and payments</li>
              <li>Communicate with you about your account and bookings</li>
              <li>Send marketing communications (with your consent)</li>
              <li>Ensure platform security and prevent fraud</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              4. Information Sharing
            </h2>
            <p className="text-slate-600 mb-4">We may share your information with:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Other Users:</strong> Hosts see Guest information for confirmed bookings; 
                  Guests see Host information for inquiries and bookings</li>
              <li><strong>Payment Processors:</strong> Stripe processes all payments</li>
              <li><strong>Service Providers:</strong> Third parties who assist our operations</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect rights</li>
            </ul>
            <p className="text-slate-600 mb-4">
              <strong>We do not sell your personal information to third parties.</strong>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              5. Data Security
            </h2>
            <p className="text-slate-600 mb-4">
              We implement industry-standard security measures including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication systems</li>
              <li>Regular security assessments</li>
              <li>Limited employee access to personal data</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              6. Your Rights
            </h2>
            <p className="text-slate-600 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate information</li>
              <li>Request deletion of your data (subject to legal retention requirements)</li>
              <li>Opt out of marketing communications</li>
              <li>Data portability (receive your data in a common format)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              7. Cookies & Tracking
            </h2>
            <p className="text-slate-600 mb-4">
              We use cookies and similar technologies to enhance your experience, analyze usage, 
              and provide personalized content. You can manage cookie preferences through your 
              browser settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              8. Data Retention
            </h2>
            <p className="text-slate-600 mb-4">
              We retain personal data for as long as your account is active or as needed to provide 
              services. We may retain certain information for legal, accounting, or fraud prevention 
              purposes after account closure.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              9. Children's Privacy
            </h2>
            <p className="text-slate-600 mb-4">
              Our platform is not intended for users under 18 years of age. We do not knowingly 
              collect personal information from children.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              10. Contact Us
            </h2>
            <p className="text-slate-600">
              For privacy-related inquiries, please contact us at:<br />
              Email: privacy@southwestflvacationrentals.com<br />
              Address: Southwest Florida Vacation Rentals, FL
            </p>
          </section>
        </div>
      </div>
    </PublicLayout>
  );
}
